import Image from 'next/image';

import { VBCTrustBadges, type RenderedTrustBadge } from './VBCTrustBadges';

export interface VBCHeroProps {
  companyName: string;
  profileTitle: string | null;
  logo: string | null;
  trustBadges: RenderedTrustBadge[];
  /** Organization badges shared across directory apps. */
  customBadges?: { name: string; imageUrl: string | null }[];
}

/**
 * VBC Hero section — company logo (or letter fallback), name, profile title,
 * trust badges row, and custom badges. Pure presentational Server Component.
 */
export function VBCHero({
  companyName,
  profileTitle,
  logo,
  trustBadges,
  customBadges = [],
}: VBCHeroProps) {
  return (
    <section
      className="mb-10 overflow-hidden rounded-xl border"
      style={{
        borderColor: 'var(--brand-card-border)',
        boxShadow: 'var(--shadow-resting)',
        backgroundColor: 'var(--brand-card-bg)',
      }}
    >
      <div className="p-6 sm:p-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
          {/* Logo */}
          <div
            className="flex h-[88px] w-[88px] flex-shrink-0 items-center justify-center overflow-hidden rounded-xl"
            style={{
              backgroundColor: 'var(--brand-card-bg, #ffffff)',
              border: '1px solid var(--brand-card-border)',
            }}
          >
            {logo ? (
              <Image
                src={logo}
                alt={`${companyName} logo`}
                width={88}
                height={88}
                className="h-full w-full object-contain p-2.5"
              />
            ) : (
              <span
                className="font-semibold"
                style={{
                  color: 'var(--brand-input-placeholder)',
                  fontSize: '32px',
                  letterSpacing: '-0.02em',
                }}
              >
                {companyName.charAt(0).toUpperCase()}
              </span>
            )}
          </div>

          {/* Name + title + badges */}
          <div className="flex-1">
            <h1
              className="font-bold"
              style={{
                color: 'var(--brand-text)',
                fontSize: 'clamp(22px, 3vw, 28px)',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              {companyName}
            </h1>

            {profileTitle && (
              <p
                className="mt-1.5"
                style={{
                  color: 'var(--brand-text-muted)',
                  fontSize: '15px',
                  fontStyle: 'italic',
                  letterSpacing: '0.01em',
                }}
              >
                {profileTitle}
              </p>
            )}

            {/* Trust badges */}
            <VBCTrustBadges badges={trustBadges} />

            {/* Custom badges */}
            {customBadges.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {customBadges.map((badge) => (
                  <span
                    key={badge.name}
                    className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1"
                    style={{
                      borderColor: 'var(--brand-card-border)',
                      fontSize: '12px',
                      fontWeight: 500,
                    }}
                  >
                    {badge.imageUrl && (
                      <Image
                        src={badge.imageUrl}
                        alt={badge.name}
                        width={16}
                        height={16}
                        className="h-4 w-4 object-contain"
                      />
                    )}
                    <span style={{ color: 'var(--brand-text-muted)' }}>
                      {badge.name}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
